import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Search, HelpCircle, Bell, Settings, User, LogOut, ChevronDown } from 'lucide-react';
import axios from 'axios';
import HelpSheet from '@/components/HelpSheet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function TopNav({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const searchDebounce = useRef(null);

  // Fetch alerts for notification badge
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await axios.get(`${API}/alerts/dashboard`, { headers: getAuthHeader() });
        // API returns array directly
        const allAlerts = Array.isArray(response.data) ? response.data : [];
        setAlerts(allAlerts.slice(0, 5));
        setUnreadCount(allAlerts.length);
      } catch (error) {
        // Silently fail
      }
    };
    fetchAlerts();
  }, [location.pathname]);

  // Get current page title
  const getPageTitle = () => {
    const path = location.pathname;
    const titles = {
      '/': 'Dashboard',
      '/equipment': 'Equipment',
      '/personnel': 'Personnel',
      '/projects': 'Projects',
      '/equipment-planner': 'Equipment Planner',
      '/personnel-planner': 'Personnel Planner',
      '/reports': 'Reports',
      '/mob-tracker': 'MOB Tracker',
      '/users': 'Users',
      '/data-management': 'Data Management',
      '/help': 'Help & Support'
    };
    return titles[path] || 'EQUIPR Horizon';
  };

  // Server-side typeahead across equipment, personnel, projects (debounced)
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    searchDebounce.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/search`, {
          headers: getAuthHeader(),
          params: { q: query.trim() },
        });
        const results = [];
        (res.data.equipment || []).forEach(e =>
          results.push({ type: 'Equipment', name: e.internal_tag || e.equipment_name, sub: [e.equipment_name, e.category].filter(Boolean).join(' • '), path: '/equipment', term: e.internal_tag || e.equipment_name }));
        (res.data.personnel || []).forEach(p =>
          results.push({ type: 'Personnel', name: p.name, sub: p.role, path: '/personnel', term: p.name }));
        (res.data.projects || []).forEach(p =>
          results.push({ type: 'Project', name: p.name, sub: p.status, path: '/projects', term: p.name }));
        setSearchResults(results);
        setShowSearchResults(true);
      } catch (error) {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 250);
  };

  const handleResultClick = (result) => {
    setShowSearchResults(false);
    setSearchQuery('');
    // Deep-link with the term so the destination page pre-filters to the item
    const suffix = result.term ? `?q=${encodeURIComponent(result.term)}` : '';
    navigate(`${result.path}${suffix}`);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tenant_branding');
    setUser(null);
    navigate('/login');
  };

  return (
    <div className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-40" data-testid="top-nav">
      {/* Left: Page Title */}
      <div className="flex items-center gap-4">
        <h1 className="text-lg font-semibold text-slate-800">{getPageTitle()}</h1>
      </div>

      {/* Center: Search */}
      <div className="flex-1 max-w-xl mx-8 relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Search equipment, personnel, projects..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
            onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
            className="pl-10 pr-4 h-9 bg-slate-50 border-slate-200 focus:bg-white"
            data-testid="global-search"
          />
        </div>
        {/* Search Results Dropdown */}
        {showSearchResults && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-50 max-h-80 overflow-y-auto">
            {searchResults.map((result, idx) => (
              <div
                key={idx}
                className="px-4 py-2 hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                onClick={() => handleResultClick(result)}
                data-testid={`global-search-result-${result.type.toLowerCase()}-${idx}`}
              >
                <div>
                  <p className="text-sm font-medium text-slate-800">{result.name}</p>
                  <p className="text-xs text-slate-500">{result.sub}</p>
                </div>
                <Badge className="text-xs bg-slate-100 text-slate-600">{result.type}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {/* Help */}
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-9 w-9 p-0" 
          title="Help & Support" 
          data-testid="help-button"
          onClick={() => setHelpOpen(true)}
        >
          <HelpCircle className="h-5 w-5 text-slate-500" />
        </Button>

        {/* Help Sheet */}
        <HelpSheet open={helpOpen} onOpenChange={setHelpOpen} />

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-9 w-9 p-0 relative" title="Notifications" data-testid="notifications-button">
              <Bell className="h-5 w-5 text-slate-500" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>Notifications</span>
              {unreadCount > 0 && <Badge className="bg-red-100 text-red-700 text-xs">{unreadCount} alerts</Badge>}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {alerts.length > 0 ? (
              <>
                {alerts.map((alert, idx) => (
                  <DropdownMenuItem key={idx} className="flex flex-col items-start py-2 cursor-pointer">
                    <div className="flex items-center gap-2 w-full">
                      <span className="text-sm font-medium">{alert.asset_name}</span>
                      <Badge className={`text-xs ${alert.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {alert.priority}
                      </Badge>
                    </div>
                    <span className="text-xs text-slate-500 mt-0.5">{alert.message}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/')} className="text-center text-sm text-blue-600 justify-center">
                  View all on Dashboard
                </DropdownMenuItem>
              </>
            ) : (
              <div className="px-4 py-6 text-center text-sm text-slate-500">
                No alerts
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Settings */}
        <Button variant="ghost" size="sm" className="h-9 w-9 p-0" title="Settings" data-testid="settings-button">
          <Settings className="h-5 w-5 text-slate-500" />
        </Button>

        {/* User Profile */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-9 px-2 gap-2" data-testid="user-menu-button">
              <div className="h-7 w-7 rounded-full bg-slate-900 flex items-center justify-center">
                <span className="text-xs font-medium text-white">{user?.email?.charAt(0).toUpperCase()}</span>
              </div>
              <div className="hidden md:flex flex-col items-start">
                <span className="text-sm font-medium text-slate-700">{user?.email}</span>
                <span className="text-xs text-slate-500">{user?.role}</span>
              </div>
              <ChevronDown className="h-4 w-4 text-slate-400 hidden md:block" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span>{user?.email}</span>
                <span className="text-xs font-normal text-slate-500">{user?.role}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <User className="mr-2 h-4 w-4" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600">
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
